import React, { useState } from 'react';
import {
  Box, ButtonBase, CircularProgress, Dialog, IconButton, useMediaQuery, useTheme,
} from '@mui/material';
import { BrokenImage as BrokenImageIcon, Close as CloseIcon } from '@mui/icons-material';
import useChatAttachmentUrl from './useChatAttachmentUrl';

const MAX_WIDTH = 260;
const MAX_HEIGHT = 320;

const PhotoViewer = ({ attachment, open, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { url, error } = useChatAttachmentUrl(attachment.id, { enabled: open });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="lg"
      PaperProps={{ sx: { bgcolor: 'common.black' } }}
      aria-label="Photo"
    >
      <IconButton
        onClick={onClose}
        aria-label="Close photo"
        sx={{ position: 'absolute', top: 8, right: 8, color: 'common.white', bgcolor: 'rgba(0,0,0,0.4)', zIndex: 1 }}
      >
        <CloseIcon />
      </IconButton>
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: { xs: '100%', sm: 300 }, minWidth: { sm: 300 },
      }}
      >
        {url && (
          <Box
            component="img"
            src={url}
            alt="Shared photo"
            sx={{ display: 'block', maxWidth: '100%', maxHeight: { xs: '100dvh', sm: '85vh' }, objectFit: 'contain' }}
          />
        )}
        {!url && !error && <CircularProgress sx={{ color: 'common.white' }} />}
        {error && <BrokenImageIcon sx={{ color: 'grey.500', fontSize: 48 }} />}
      </Box>
    </Dialog>
  );
};

/** Thumbnail sized from the stored dimensions (no layout jump); tap to view full size. */
const ChatPhoto = ({ attachment }) => {
  const [open, setOpen] = useState(false);
  const { url, error } = useChatAttachmentUrl(attachment.id, { thumbnail: true });

  const ratio = attachment.width > 0 && attachment.height > 0 ? attachment.width / attachment.height : 4 / 3;
  const width = Math.round(Math.min(MAX_WIDTH, MAX_HEIGHT * ratio, attachment.width || MAX_WIDTH));

  return (
    <>
      <ButtonBase
        onClick={() => setOpen(true)}
        aria-label="Open photo"
        sx={{
          display: 'block',
          width,
          maxWidth: '100%',
          aspectRatio: `${ratio}`,
          borderRadius: 1.5,
          overflow: 'hidden',
          bgcolor: 'grey.200',
        }}
      >
        {url ? (
          <Box component="img" src={url} alt="Shared photo" sx={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%',
          }}
          >
            {error ? <BrokenImageIcon color="disabled" /> : <CircularProgress size={20} />}
          </Box>
        )}
      </ButtonBase>
      {open && <PhotoViewer attachment={attachment} open={open} onClose={() => setOpen(false)} />}
    </>
  );
};

export default ChatPhoto;
